import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";
import liteIcon from "../assets/icons/LiteIcon.png";
import { toast } from "react-toastify";
import {
  FaArrowLeft,
  FaEye,
  FaEyeSlash,
  FaUser,
  FaEnvelope,
  FaGlobe,
  FaMapMarkerAlt,
  FaLock,
  FaBuilding,
} from "react-icons/fa";
import { CircularProgress } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";

const FloatingIcon = ({ emoji, style, delay = 0, duration = 6 }) => (
  <motion.div
    style={{
      position: "absolute",
      fontSize: "clamp(20px, 2.4vw, 32px)",
      filter: "drop-shadow(0 6px 16px rgba(0,0,0,0.2))",
      userSelect: "none",
      zIndex: 2,
      ...style,
    }}
    animate={{ y: [0, -18, 0], rotate: [-4, 4, -4], scale: [1, 1.08, 1] }}
    transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
  >
    {emoji}
  </motion.div>
);

const Ring = ({
  size,
  color,
  top,
  left,
  right,
  bottom,
  delay = 0,
  duration = 10,
  dashed,
}) => (
  <motion.div
    style={{
      position: "absolute",
      width: size,
      height: size,
      borderRadius: "50%",
      border: `${dashed ? "2px dashed" : "1.5px solid"} ${color}`,
      top,
      left,
      right,
      bottom,
      pointerEvents: "none",
    }}
    animate={{ rotate: 360 }}
    transition={{ duration, delay, repeat: Infinity, ease: "linear" }}
  />
);

const Pulse = ({ style, delay = 0, color = "rgba(255,255,255,0.5)" }) => (
  <motion.div
    style={{
      width: 9,
      height: 9,
      borderRadius: "50%",
      background: color,
      position: "absolute",
      ...style,
    }}
    animate={{ scale: [1, 2.2, 1], opacity: [0.7, 0, 0.7] }}
    transition={{ duration: 3.2, delay, repeat: Infinity, ease: "easeInOut" }}
  />
);

const FieldInput = ({
  icon: Icon,
  label,
  name,
  type = "text",
  value,
  onChange,
  onFocus,
  onBlur,
  focused,
  error,
  suffix,
  placeholder,
}) => (
  <div>
    <label
      style={{
        display: "block",
        fontSize: "0.66rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? "#ef4444" : focused ? "#0284c7" : "#64748b",
        marginBottom: 6,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: "color 0.25s",
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          left: 14,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          color: error ? "#f87171" : focused ? "#0ea5e9" : "#94a3b8",
          display: "flex",
          alignItems: "center",
          transition: "color 0.25s",
        }}
      >
        <Icon size={13} />
      </div>
      <motion.input
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        required
        placeholder={placeholder || label}
        animate={{
          boxShadow: error
            ? "0 0 0 3px rgba(239,68,68,0.15)"
            : focused
            ? "0 0 0 3px rgba(14,165,233,0.18), 0 4px 20px rgba(14,165,233,0.1)"
            : "0 1px 4px rgba(0,0,0,0.05)",
        }}
        style={{
          width: "100%",
          padding: "12px 40px 12px 38px",
          borderRadius: 11,
          border: error
            ? "2px solid #f87171"
            : focused
            ? "2px solid #0ea5e9"
            : "2px solid #e2e8f0",
          background: error
            ? "rgba(254,242,242,0.8)"
            : focused
            ? "rgba(240,249,255,0.9)"
            : "#f8fafc",
          fontSize: "0.88rem",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: "#0f172a",
          outline: "none",
          transition: "border-color 0.25s, background 0.25s",
          boxSizing: "border-box",
        }}
      />
      {suffix && (
        <div
          style={{
            position: "absolute",
            right: 12,
            top: "50%",
            transform: "translateY(-50%)",
          }}
        >
          {suffix}
        </div>
      )}
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          style={{
            color: "#ef4444",
            fontSize: "0.72rem",
            marginTop: 5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
          }}
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const SelectField = ({
  icon: Icon,
  label,
  name,
  value,
  onChange,
  onFocus,
  onBlur,
  focused,
  error,
  options,
  disabled,
  placeholder,
}) => (
  <div>
    <label
      style={{
        display: "block",
        fontSize: "0.66rem",
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: error ? "#ef4444" : focused ? "#0284c7" : "#64748b",
        marginBottom: 6,
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        transition: "color 0.25s",
      }}
    >
      {label}
    </label>
    <div style={{ position: "relative" }}>
      <div
        style={{
          position: "absolute",
          left: 14,
          top: "50%",
          transform: "translateY(-50%)",
          zIndex: 1,
          color: disabled
            ? "#c8d3df"
            : error
            ? "#f87171"
            : focused
            ? "#0ea5e9"
            : "#94a3b8",
          display: "flex",
          alignItems: "center",
          transition: "color 0.25s",
          pointerEvents: "none",
        }}
      >
        <Icon size={13} />
      </div>
      <motion.select
        name={name}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        required
        animate={{
          boxShadow: error
            ? "0 0 0 3px rgba(239,68,68,0.15)"
            : focused
            ? "0 0 0 3px rgba(14,165,233,0.18)"
            : "0 1px 4px rgba(0,0,0,0.05)",
        }}
        style={{
          width: "100%",
          padding: "12px 36px 12px 38px",
          borderRadius: 11,
          border: error
            ? "2px solid #f87171"
            : focused
            ? "2px solid #0ea5e9"
            : "2px solid #e2e8f0",
          background: disabled
            ? "#f1f5f9"
            : focused
            ? "rgba(240,249,255,0.9)"
            : "#f8fafc",
          fontSize: "0.88rem",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          color: value ? "#0f172a" : "#94a3b8",
          outline: "none",
          transition: "all 0.25s",
          boxSizing: "border-box",
          appearance: "none",
          cursor: disabled ? "not-allowed" : "pointer",
        }}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o.id} value={o.id} style={{ color: "#0f172a" }}>
            {o.name || o.state}
          </option>
        ))}
      </motion.select>
      <div
        style={{
          position: "absolute",
          right: 13,
          top: "50%",
          transform: "translateY(-50%)",
          color: "#94a3b8",
          fontSize: "0.65rem",
          pointerEvents: "none",
        }}
      >
        ▼
      </div>
    </div>
    <AnimatePresence>
      {error && (
        <motion.p
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          style={{
            color: "#ef4444",
            fontSize: "0.72rem",
            marginTop: 5,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontWeight: 600,
          }}
        >
          {error}
        </motion.p>
      )}
    </AnimatePresence>
  </div>
);

const RegisterPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
    country: "",
    location: "",
    isInst: true,
  });
  const [countries, setCountries] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    document.title = "Register - LiteUpper";
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener("resize", check);
    axios
      .get(`${API_URL}/countries/`)
      .then((r) => setCountries(r.data))
      .catch(console.error);
    return () => window.removeEventListener("resize", check);
  }, []);

  const handleCountryChange = (e) => {
    const id = e.target.value;
    setFormData((p) => ({ ...p, country: id, location: "" }));
    setLocations([]);
    if (id)
      axios
        .get(`${API_URL}/countries/${id}/locations/`)
        .then((r) => setLocations(r.data))
        .catch(console.error);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((p) => ({
      ...p,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
  };

  const validate = () => {
    const err = {};
    if (!formData.username.trim()) err.username = "Username required";
    if (!formData.email.trim()) err.email = "Email required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) err.email = "Invalid email";
    if (!formData.password) err.password = "Password required";
    else if (formData.password.length < 6) err.password = "Min 6 characters";
    if (!formData.confirmPassword)
      err.confirmPassword = "Confirm your password";
    else if (formData.password !== formData.confirmPassword)
      err.confirmPassword = "Passwords do not match";
    if (!formData.country) err.country = "Select a country";
    if (!formData.location) err.location = "Select a location";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/create-user/`, {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        confirm_password: formData.confirmPassword,
        is_inst: formData.isInst,
        country: formData.country,
        location: formData.location,
      });
      toast.success("Registered! Check your email.");
      navigate("/verification");
    } catch (err) {
      const data = err.response?.data;
      if (data?.errors) {
        setErrors(data.errors);
        const firstKey = Object.keys(data.errors)[0];
        toast.error(data.errors[firstKey]?.[0] || "Fix errors");
      } else toast.error("Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const E = [0.16, 1, 0.3, 1];
  const stagger = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.07, delayChildren: 0.2 } },
  };
  const item = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: E } },
  };
  const fadeUp = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: E } },
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: #f8fafc; overflow-x: hidden; }
        input:-webkit-autofill { -webkit-box-shadow: 0 0 0 100px #f8fafc inset !important; -webkit-text-fill-color: #0f172a !important; }
        select option { color: #0f172a; background: #fff; }
        ::-webkit-scrollbar { width: 5px; } ::-webkit-scrollbar-thumb { background: #bae6fd; border-radius: 8px; }
      `}</style>

      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          fontFamily: "'Plus Jakarta Sans', sans-serif",
          background: "#f8fafc",
        }}
      >
        {/* ══ LEFT PANEL – desktop only ══ */}
        {!isMobile && (
          <motion.div
            initial={{ opacity: 0, x: -90 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 1.1, ease: E }}
            style={{
              flex: "0 0 40%",
              minHeight: "100vh",
              background:
                "linear-gradient(150deg, #0369a1 0%, #0284c7 30%, #0ea5e9 65%, #38bdf8 100%)",
              position: "relative",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "48px 36px",
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                backgroundImage:
                  "radial-gradient(circle, rgba(255,255,255,0.08) 1px, transparent 1px)",
                backgroundSize: "30px 30px",
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: "20%",
                left: "15%",
                width: 320,
                height: 320,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 65%)",
                pointerEvents: "none",
              }}
            />

            <Ring
              size={380}
              color="rgba(255,255,255,0.07)"
              top="-80px"
              left="-80px"
              duration={24}
            />
            <Ring
              size={240}
              color="rgba(255,255,255,0.1)"
              top="55%"
              left="45%"
              duration={18}
              delay={2}
            />
            <Ring
              size={170}
              color="rgba(251,191,36,0.28)"
              bottom="-40px"
              right="-40px"
              duration={13}
              delay={3}
              dashed
            />
            <Ring
              size={110}
              color="rgba(255,255,255,0.15)"
              top="5%"
              left="60%"
              duration={9}
              delay={1}
            />
            <Pulse style={{ top: "18%", left: "10%" }} delay={0} />
            <Pulse style={{ top: "62%", left: "85%" }} delay={1} />
            <Pulse
              style={{ top: "82%", left: "25%" }}
              delay={2}
              color="rgba(251,191,36,0.8)"
            />
            <Pulse style={{ top: "10%", left: "80%" }} delay={3} />
            <FloatingIcon
              emoji="📚"
              style={{ top: "7%", left: "7%" }}
              delay={0}
              duration={5.5}
            />
            <FloatingIcon
              emoji="🎓"
              style={{ top: "10%", right: "7%" }}
              delay={0.9}
              duration={6.5}
            />
            <FloatingIcon
              emoji="✏️"
              style={{ top: "50%", left: "3%" }}
              delay={1.7}
              duration={5}
            />
            <FloatingIcon
              emoji="🔬"
              style={{ bottom: "22%", right: "5%" }}
              delay={0.5}
              duration={7}
            />
            <FloatingIcon
              emoji="🧮"
              style={{ bottom: "8%", left: "12%" }}
              delay={2.1}
              duration={6.2}
            />
            <FloatingIcon
              emoji="🏆"
              style={{ top: "38%", right: "3%" }}
              delay={1.3}
              duration={5.8}
            />

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.8, duration: 1.4, ease: E }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 5,
                background:
                  "linear-gradient(90deg,#f59e0b,#fbbf24,#fde68a,#fbbf24,#f59e0b)",
                transformOrigin: "left",
              }}
            />

            <div
              style={{
                position: "relative",
                zIndex: 3,
                textAlign: "center",
                maxWidth: 340,
              }}
            >
              <motion.div
                initial={{ scale: 0, rotate: -30 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{
                  delay: 0.4,
                  duration: 1,
                  type: "spring",
                  stiffness: 140,
                }}
              >
                <Link to="/" style={{ display: "inline-block" }}>
                  <motion.div
                    whileHover={{ scale: 1.08, rotate: 3 }}
                    transition={{ type: "spring", stiffness: 250 }}
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: 28,
                      background: "rgba(255,255,255,0.18)",
                      backdropFilter: "blur(20px)",
                      border: "2px solid rgba(255,255,255,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      margin: "0 auto 28px",
                      boxShadow:
                        "0 20px 60px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.4)",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={liteIcon}
                      alt="LiteUpper"
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: "contain",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  </motion.div>
                </Link>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 32 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.9, ease: E }}
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: "clamp(28px, 3.2vw, 44px)",
                  fontWeight: 900,
                  color: "#fff",
                  lineHeight: 1.12,
                  marginBottom: 16,
                  letterSpacing: "-0.5px",
                  textShadow: "0 2px 20px rgba(0,0,0,0.12)",
                }}
              >
                Begin Your
                <span
                  style={{
                    display: "block",
                    background:
                      "linear-gradient(135deg, #fde68a, #fbbf24, #f59e0b)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  Learning Journey
                </span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.8, ease: E }}
                style={{
                  color: "rgba(255,255,255,0.84)",
                  fontSize: "0.98rem",
                  lineHeight: 1.75,
                  marginBottom: 32,
                  fontWeight: 500,
                }}
              >
                Create your free account and join a community of achievers
                reaching their academic goals.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.88 }}
                style={{ display: "flex", flexDirection: "column", gap: 12 }}
              >
                {[
                  { emoji: "🎯", text: "Personalized learning paths" },
                  { emoji: "📊", text: "Track your progress daily" },
                  { emoji: "🏅", text: "Earn certificates & badges" },
                  { emoji: "👥", text: "Learn with top instructors" },
                ].map((f) => (
                  <motion.div
                    key={f.text}
                    whileHover={{ x: 6 }}
                    transition={{ type: "spring", stiffness: 300 }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      background: "rgba(255,255,255,0.12)",
                      backdropFilter: "blur(12px)",
                      border: "1px solid rgba(255,255,255,0.2)",
                      borderRadius: 12,
                      padding: "10px 16px",
                      cursor: "default",
                    }}
                  >
                    <span style={{ fontSize: 18 }}>{f.emoji}</span>
                    <span
                      style={{
                        color: "rgba(255,255,255,0.88)",
                        fontSize: "0.85rem",
                        fontWeight: 600,
                      }}
                    >
                      {f.text}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.1 }}
              style={{
                position: "absolute",
                bottom: 32,
                left: 36,
                right: 36,
                borderLeft: "3px solid #fbbf24",
                paddingLeft: 16,
              }}
            >
              <p
                style={{
                  color: "rgba(255,255,255,0.65)",
                  fontSize: "0.78rem",
                  fontStyle: "italic",
                  fontFamily: "'Playfair Display', serif",
                  lineHeight: 1.6,
                }}
              >
                "The beautiful thing about learning is that nobody can take it
                away from you."
              </p>
              <p
                style={{
                  color: "rgba(255,255,255,0.4)",
                  fontSize: "0.68rem",
                  marginTop: 5,
                  fontWeight: 700,
                }}
              >
                — B.B. King
              </p>
            </motion.div>
          </motion.div>
        )}

        {/* ══ RIGHT PANEL ══ */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            padding: isMobile ? "70px 16px 40px" : "32px 24px",
            position: "relative",
            background: "#f8fafc",
            minHeight: "100vh",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundImage:
                "radial-gradient(circle at 85% 10%, rgba(14,165,233,0.07) 0%, transparent 45%), radial-gradient(circle at 10% 90%, rgba(251,191,36,0.05) 0%, transparent 40%)",
              pointerEvents: "none",
              zIndex: 0,
            }}
          />

          {/* Back */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            onClick={() => navigate(-1)}
            whileHover={{ x: -3 }}
            whileTap={{ scale: 0.92 }}
            style={{
              position: "fixed",
              top: 20,
              left: 20,
              zIndex: 100,
              display: "flex",
              alignItems: "center",
              gap: 7,
              background: "rgba(255,255,255,0.95)",
              border: "1.5px solid rgba(14,165,233,0.25)",
              borderRadius: 12,
              padding: "9px 16px",
              color: "#0369a1",
              fontSize: "0.8rem",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              backdropFilter: "blur(12px)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
            }}
          >
            <FaArrowLeft size={11} /> Back
          </motion.button>

          {/* Card */}
          <motion.div
            style={{
              width: "100%",
              maxWidth: 500,
              position: "relative",
              zIndex: 1,
            }}
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.9, ease: E, delay: 0.15 }}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: isMobile ? 20 : 26,
                boxShadow:
                  "0 32px 100px rgba(3,105,161,0.12), 0 4px 32px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.9)",
                border: "1px solid rgba(14,165,233,0.1)",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: 4,
                  background:
                    "linear-gradient(90deg, #0369a1, #0284c7, #38bdf8, #fbbf24)",
                }}
              />

              <div
                style={{
                  padding: isMobile ? "28px 20px 28px" : "36px 40px 36px",
                }}
              >
                {/* Logo */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.28, duration: 0.6, type: "spring" }}
                  style={{ textAlign: "center", marginBottom: 20 }}
                >
                  <Link to="/">
                    <motion.img
                      src={liteIcon}
                      alt="LiteUpper"
                      whileHover={{ scale: 1.1, rotate: 4 }}
                      transition={{ type: "spring", stiffness: 260 }}
                      style={{
                        height: isMobile ? 64 : 76,
                        width: isMobile ? 64 : 76,
                        objectFit: "contain",
                        filter: "drop-shadow(0 8px 20px rgba(14,165,233,0.32))",
                        cursor: "pointer",
                      }}
                    />
                  </Link>
                </motion.div>

                {/* Header */}
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.38 }}
                  style={{ textAlign: "center", marginBottom: 24 }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 7,
                      background: "linear-gradient(135deg, #f0f9ff, #e0f2fe)",
                      border: "1px solid rgba(14,165,233,0.22)",
                      borderRadius: 100,
                      padding: "7px 16px",
                      marginBottom: 12,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>✨</span>
                    <span
                      style={{
                        fontSize: "0.67rem",
                        fontWeight: 700,
                        color: "#0369a1",
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                      }}
                    >
                      New Account
                    </span>
                  </div>
                  <h2
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: isMobile ? "1.5rem" : "1.8rem",
                      fontWeight: 900,
                      color: "#0c1a2e",
                      letterSpacing: "-0.4px",
                      lineHeight: 1.2,
                      marginBottom: 6,
                    }}
                  >
                    Join LiteUpper
                  </h2>
                  <p
                    style={{
                      color: "#64748b",
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  >
                    Start your smarter learning journey today
                  </p>
                </motion.div>

                <form onSubmit={handleRegister}>
                  <motion.div
                    variants={stagger}
                    initial="hidden"
                    animate="visible"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 14,
                    }}
                  >
                    <motion.div variants={item}>
                      <FieldInput
                        icon={FaUser}
                        label="Username"
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        focused={focused === "username"}
                        onFocus={() => setFocused("username")}
                        onBlur={() => setFocused(null)}
                        error={errors.username}
                      />
                    </motion.div>

                    <motion.div variants={item}>
                      <FieldInput
                        icon={FaEnvelope}
                        label="Email Address"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        focused={focused === "email"}
                        onFocus={() => setFocused("email")}
                        onBlur={() => setFocused(null)}
                        error={errors.email}
                      />
                    </motion.div>

                    <motion.div variants={item}>
                      <SelectField
                        icon={FaGlobe}
                        label="Country"
                        name="country"
                        value={formData.country}
                        onChange={handleCountryChange}
                        focused={focused === "country"}
                        onFocus={() => setFocused("country")}
                        onBlur={() => setFocused(null)}
                        error={errors.country}
                        options={countries}
                        placeholder="Select your country"
                      />
                    </motion.div>

                    <motion.div variants={item}>
                      <SelectField
                        icon={FaMapMarkerAlt}
                        label="State / Location"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        focused={focused === "location"}
                        onFocus={() => setFocused("location")}
                        onBlur={() => setFocused(null)}
                        error={errors.location}
                        options={locations}
                        disabled={!formData.country}
                        placeholder={
                          !formData.country
                            ? "Select country first"
                            : "Select state / location"
                        }
                      />
                    </motion.div>

                    {/* Password row */}
                    <motion.div
                      variants={item}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 12,
                      }}
                    >
                      <FieldInput
                        icon={FaLock}
                        label="Password"
                        name="password"
                        type={showPass ? "text" : "password"}
                        value={formData.password}
                        onChange={handleChange}
                        focused={focused === "password"}
                        onFocus={() => setFocused("password")}
                        onBlur={() => setFocused(null)}
                        error={errors.password}
                        suffix={
                          <motion.button
                            type="button"
                            onClick={() => setShowPass((p) => !p)}
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.88 }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: showPass ? "#0ea5e9" : "#94a3b8",
                              display: "flex",
                              padding: 3,
                              transition: "color 0.2s",
                            }}
                          >
                            {showPass ? (
                              <FaEyeSlash size={13} />
                            ) : (
                              <FaEye size={13} />
                            )}
                          </motion.button>
                        }
                      />
                      <FieldInput
                        icon={FaLock}
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirm ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        focused={focused === "confirmPassword"}
                        onFocus={() => setFocused("confirmPassword")}
                        onBlur={() => setFocused(null)}
                        error={errors.confirmPassword}
                        placeholder="Confirm"
                        suffix={
                          <motion.button
                            type="button"
                            onClick={() => setShowConfirm((p) => !p)}
                            whileHover={{ scale: 1.15 }}
                            whileTap={{ scale: 0.88 }}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              color: showConfirm ? "#0ea5e9" : "#94a3b8",
                              display: "flex",
                              padding: 3,
                              transition: "color 0.2s",
                            }}
                          >
                            {showConfirm ? (
                              <FaEyeSlash size={13} />
                            ) : (
                              <FaEye size={13} />
                            )}
                          </motion.button>
                        }
                      />
                    </motion.div>

                    {/* Institution toggle */}
                    <motion.div variants={fadeUp}>
                      <motion.div
                        onClick={() =>
                          setFormData((p) => ({ ...p, isInst: !p.isInst }))
                        }
                        animate={{
                          background: formData.isInst
                            ? "rgba(2,132,199,0.06)"
                            : "rgba(248,250,252,1)",
                          borderColor: formData.isInst
                            ? "rgba(14,165,233,0.35)"
                            : "#e2e8f0",
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "14px 16px",
                          borderRadius: 12,
                          border: "2px solid",
                          cursor: "pointer",
                          transition: "all 0.3s",
                        }}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <motion.div
                            animate={{
                              background: formData.isInst
                                ? "linear-gradient(135deg, #0284c7, #0ea5e9)"
                                : "rgba(14,165,233,0.08)",
                              boxShadow: formData.isInst
                                ? "0 4px 14px rgba(2,132,199,0.35)"
                                : "none",
                            }}
                            style={{
                              width: 36,
                              height: 36,
                              borderRadius: 10,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.3s",
                            }}
                          >
                            <FaBuilding
                              size={14}
                              color={formData.isInst ? "#fff" : "#94a3b8"}
                            />
                          </motion.div>
                          <div>
                            <p
                              style={{
                                fontSize: "0.875rem",
                                fontWeight: 700,
                                color: "#0c1a2e",
                                lineHeight: 1.2,
                              }}
                            >
                              Register as Institution
                            </p>
                            <p
                              style={{
                                fontSize: "0.73rem",
                                color: "#64748b",
                                marginTop: 2,
                              }}
                            >
                              Schools, training centres & academies
                            </p>
                          </div>
                        </div>
                        {/* Toggle switch */}
                        <motion.div
                          animate={{
                            background: formData.isInst ? "#0284c7" : "#e2e8f0",
                          }}
                          style={{
                            width: 44,
                            height: 24,
                            borderRadius: 12,
                            position: "relative",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                          transition={{ duration: 0.25 }}
                        >
                          <motion.div
                            animate={{ x: formData.isInst ? 22 : 2 }}
                            transition={{
                              type: "spring",
                              stiffness: 400,
                              damping: 30,
                            }}
                            style={{
                              position: "absolute",
                              top: 2,
                              width: 20,
                              height: 20,
                              borderRadius: "50%",
                              background: "#fff",
                              boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                            }}
                          />
                        </motion.div>
                      </motion.div>
                    </motion.div>

                    {/* Submit */}
                    <motion.div variants={fadeUp}>
                      <motion.button
                        type="submit"
                        disabled={loading}
                        whileHover={
                          !loading
                            ? {
                                scale: 1.02,
                                boxShadow: "0 20px 50px rgba(2,132,199,0.5)",
                              }
                            : {}
                        }
                        whileTap={!loading ? { scale: 0.97 } : {}}
                        style={{
                          width: "100%",
                          padding: "15px 24px",
                          borderRadius: 14,
                          border: "none",
                          background: loading
                            ? "rgba(14,165,233,0.55)"
                            : "linear-gradient(135deg, #0369a1 0%, #0284c7 45%, #0ea5e9 100%)",
                          color: "#fff",
                          fontSize: "0.975rem",
                          fontWeight: 800,
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          cursor: loading ? "not-allowed" : "pointer",
                          boxShadow: "0 10px 32px rgba(2,132,199,0.38)",
                          transition: "box-shadow 0.25s",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 10,
                          letterSpacing: "0.02em",
                        }}
                      >
                        <AnimatePresence mode="wait">
                          {loading ? (
                            <motion.span
                              key="l"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                              }}
                            >
                              <CircularProgress
                                size={17}
                                sx={{ color: "#fff" }}
                              />{" "}
                              Creating account…
                            </motion.span>
                          ) : (
                            <motion.span
                              key="t"
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              exit={{ opacity: 0, y: -8 }}
                            >
                              Create Account →
                            </motion.span>
                          )}
                        </AnimatePresence>
                      </motion.button>
                    </motion.div>

                    <motion.div
                      variants={fadeUp}
                      style={{ textAlign: "center", paddingBottom: 4 }}
                    >
                      <span style={{ color: "#64748b", fontSize: "0.875rem" }}>
                        Already have an account?{" "}
                      </span>
                      <motion.button
                        type="button"
                        onClick={() => navigate("/login")}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.96 }}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontWeight: 800,
                          color: "#0284c7",
                          fontSize: "0.875rem",
                          fontFamily: "'Plus Jakarta Sans', sans-serif",
                          padding: 0,
                          textDecoration: "underline",
                          textDecorationColor: "rgba(2,132,199,0.3)",
                          textUnderlineOffset: 3,
                        }}
                      >
                        Sign in
                      </motion.button>
                    </motion.div>
                  </motion.div>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default RegisterPage;
